import { type JSX, useEffect, useState } from "react";

import { CampaignsEmptyPlaceholder } from "features/campaigns/components";
import {
  MobileHeading,
  NavTabsContentContainer,
  TableAndFiltersContainer,
} from "features/layout/components";

import { ColumnsVisibilityControl } from "common/components/DataTable";
import useColumnVisibility from "common/components/DataTable/useColumnVisibility";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useCampaigns from "common/datahooks/useCampaigns";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Campaign } from "common/types";

import { CAMPAIGNS_COLUMNS, CAMPAIGNS_TAB_ITEMS } from "../../constants";
import useCampaignsFilters from "../../hooks/useCampaignsFilters";
import CreateCampaignButton from "./CreateCampaignButton";
import DesktopFilters from "./DesktopLayout/DesktopFilters";
import DesktopTable from "./DesktopLayout/DesktopTable";
import ManageCampaigns from "./MobileLayout/ManageCampaigns";
import MobileFilters from "./MobileLayout/MobileFilters";
import MobileList from "./MobileLayout/MobileList";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function CampaignsList() {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);

  const { campaigns, isLoadingCampaigns, campaignsError, refetchCampaigns } =
    useCampaigns();

  const {
    searchParams,
    setSearchParams,
    filteredCampaigns,
    hasSearchParams,
    searchText,
    setSearchText,
    clearSearchParams,
  } = useCampaignsFilters(campaigns);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    "campaigns",
    CAMPAIGNS_COLUMNS,
  );

  useEffect(() => {
    if (filteredCampaigns) {
      setSelectedCampaigns((prev) =>
        prev
          .map((selectedCampaign) =>
            filteredCampaigns.find(
              (campaign) => campaign.id === selectedCampaign.id,
            ),
          )
          .filter((campaign) => campaign != null),
      );
    }
  }, [filteredCampaigns]);

  const searchInputComponent = (
    <SearchInput
      className="max-lg:flex-1 lg:max-w-72"
      placeholder="Search campaigns"
      value={searchText}
      variant={isDesktop ? "sm" : "lg"}
      onChange={(event) => setSearchText(event.target.value)}
      onClear={() => setSearchText("")}
    />
  );

  let mainContent: JSX.Element;
  if (campaignsError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display Campaigns"
        onRetry={refetchCampaigns}
      />
    );
  } else if (!isLoadingCampaigns && (!campaigns || !campaigns.length)) {
    mainContent = <CampaignsEmptyPlaceholder />;
  } else {
    let content = (
      <EmptyPlaceholder
        title="No campaigns found"
        subtitle="There were no results that match your criteria"
        imageSrc={NoResultsImage}
        actionComponent={
          <Button
            onClick={() => {
              setSearchText("");
              clearSearchParams();
            }}
          >
            Clear
          </Button>
        }
      />
    );

    if (isLoadingCampaigns || filteredCampaigns.length) {
      const commonProps = {
        campaigns: filteredCampaigns,
        selectedCampaigns,
        setSelectedCampaigns,
      };
      content = isDesktop ? (
        <DesktopTable
          {...commonProps}
          isLoading={isLoadingCampaigns}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
        />
      ) : (
        <MobileList {...commonProps} />
      );
    }

    mainContent = isDesktop ? (
      <TableAndFiltersContainer
        searchComponent={searchInputComponent}
        filtersComponent={
          <DesktopFilters
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            clearAllFilters={hasSearchParams && clearSearchParams}
          />
        }
        columnSelectionComponent={
          <ColumnsVisibilityControl
            columns={CAMPAIGNS_COLUMNS}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
          />
        }
      >
        {content}
      </TableAndFiltersContainer>
    ) : (
      <>
        <MobileHeading
          actionComponent={
            <>
              <CreateCampaignButton />
              {!!selectedCampaigns.length && (
                <RestrictedComponent visibleFor={["campaigns_full"]}>
                  <ManageCampaigns
                    selectedCampaigns={selectedCampaigns}
                    resetSelectedCampaigns={() => setSelectedCampaigns([])}
                  />
                </RestrictedComponent>
              )}
            </>
          }
          searchComponent={
            <>
              {searchInputComponent}
              <MobileFilters
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                clearSearchParams={hasSearchParams && clearSearchParams}
              />
            </>
          }
        />
        {content}
      </>
    );
  }

  return (
    <NavTabsContentContainer
      desktopActionComponent={<CreateCampaignButton />}
      tabItems={CAMPAIGNS_TAB_ITEMS}
    >
      {mainContent}
    </NavTabsContentContainer>
  );
}
